// Hợp đồng API của màn «Tải video» (dán link kênh / link video → máy công ty tải về, giữ nguyên file gốc).
import { z } from 'zod';
import { extractYoutubeVideoId } from './music-live.js';
import { YOUTUBE_DESCRIPTION_MAX, YOUTUBE_TAGS_MAX, YOUTUBE_TITLE_MAX, tagsLength, } from './youtube-publish.js';
/**
 * TẢI VIDEO (28/10/2026, chủ dự án: «Tôi chỉ cần chức năng tải video về máy không cần gì khác … Bỏ chức năng dựng
 * lại video»). Thay cho màn «Dựng lại video» (24/10/2026, bdd0f92) và chế độ «Tải video» chồng lên nó (427e97f):
 * còn đúng MỘT việc — dán link kênh (kênh công ty đã nối ở Đăng video) hoặc link video, máy công ty tải file gốc về
 * thư mục, kèm `- seo.txt` (tiêu đề · mô tả · tag gốc) và `- thumb.jpg` đúng bộ file của tool để kéo vào Tải lên
 * của Đăng video được.
 *
 * Việc nặng (tải từ YouTube) KHÔNG chạy trên máy chủ web: máy chủ thuê ổ nhỏ, và YouTube chặn tải từ địa chỉ trung
 * tâm dữ liệu («Sign in to confirm you're not a bot»). Nó chạy trên MÁY CÔNG TY đã có cầu nối GPM (xem `gpm.ts`):
 * web xếp việc → máy chủ gửi lệnh `remake` (tên lệnh giữ từ bản cầu nối 6/7, cầu nối không cần đổi) xuống cầu nối →
 * cầu nối mở một tiến trình «thợ» riêng → thợ tải bằng yt-dlp, GIỮ NGUYÊN file (`render.keepOriginal`, cầu nối bản 7:
 * mp4 đổi tên, mkv/webm đóng gói lại `ffmpeg -c copy`, không mã hoá lại), ghi bộ file rồi báo tiến độ về máy chủ.
 *
 * Chỉ dùng cho video công ty có quyền (nội dung của mình, đã mua / được cấp phép): app không kiểm được bản quyền
 * thay người dùng. Link kênh chỉ nhận kênh đã nối ở Đăng video (26/10/2026) — không tải trọn kênh người khác.
 */
/** Cầu nối từ bản này mới giữ nguyên được file (bản 6 sẽ mã hoá lại) — máy chạy bản cũ phải tải lại file cầu nối. */
export const GPM_BRIDGE_DOWNLOAD_VERSION = 7;
/** Số video một lượt xếp việc (dán link kênh có thể tích cả trăm video). */
export const DOWNLOAD_MAX_LINKS = 100;
/** Việc đang chạy mà ngần này phút không báo tiến độ (máy tắt, thợ bị diệt) → báo hỏng. */
export const DOWNLOAD_STALE_MINUTES = 20;
/** Số việc gần nhất trả về cho bảng. */
export const DOWNLOAD_HISTORY_LIMIT = 200;
/** Thư mục mặc định do cầu nối tự đặt khi ô thư mục để trống: `<gốc Trình duyệt HALO>\_remake\<tên kênh gốc>`. */
export const DOWNLOAD_DEFAULT_OUTPUT_HINT = 'E:\\HALO-Browser\\_remake\\<tên kênh>';
export const DOWNLOAD_HEIGHTS = [720, 1080, 1440, 2160];
export const DOWNLOAD_CHANNEL_LIMITS = [100, 200, 500];
const text = (max) => z.string().trim().max(max);
const VIDEO_ID_RE = /^[\w-]{11}$/;
const heightSchema = z.union([z.literal(720), z.literal(1080), z.literal(1440), z.literal(2160)]);
export function downloadRenderSpec(maxHeight) {
    return {
        keepOriginal: true,
        maxHeight,
        encoder: 'x264',
        quality: 20,
        speed: 'medium',
        fps: 0,
        audioKbps: 192,
        logo: { assetId: null, position: 'tr', widthPct: 14, marginPct: 2, opacity: 1 },
        cover: { enabled: false, method: 'blur', x: 84, y: 3, w: 14, h: 12, color: '#000000' },
        frame: { assetId: null },
        outro: { assetId: null, transition: 'cut', fadeSeconds: 1 },
    };
}
/** Chất lượng tải ghi trong thông số đã lưu của việc (jsonb); thiếu / lạ → 1080. */
export function maxHeightOf(render) {
    const value = typeof render === 'object' && render !== null
        ? render.maxHeight
        : undefined;
    return DOWNLOAD_HEIGHTS.includes(Number(value))
        ? Number(value)
        : 1080;
}
// ---------------------------------------------------------------- việc
/** `uploading` chỉ còn ở việc cũ của Dựng lại video (đích Studio) — giữ để bảng đọc được lịch sử. */
export const DOWNLOAD_STAGES = [
    'queued',
    'downloading',
    'rendering',
    'ready',
    'uploading',
    'done',
    'failed',
    'cancelled',
];
export const DOWNLOAD_STAGE_LABELS = {
    queued: 'Chờ máy nhận',
    downloading: 'Đang tải',
    rendering: 'Đang ghi file',
    ready: 'Đã tải xong',
    uploading: 'Đang tải lên',
    done: 'Đã tải xong',
    failed: 'Lỗi',
    cancelled: 'Đã huỷ',
};
/** Việc còn chạy (máy chủ còn chờ tiến độ). */
export const DOWNLOAD_ACTIVE_STAGES = new Set([
    'queued',
    'downloading',
    'rendering',
    'ready',
    'uploading',
]);
export const isDownloadActive = (stage) => DOWNLOAD_ACTIVE_STAGES.has(stage);
export const downloadInspectSchema = z.object({
    links: z.array(text(500)).min(1).max(DOWNLOAD_MAX_LINKS),
});
/**
 * LINK KÊNH (26/10/2026): chỉ kênh công ty đã nối ở Đăng video — máy chủ đọc danh sách video bằng mã uỷ quyền của
 * chính kênh ấy (mọi trạng thái, như phần Video của Đăng video), chỉ Giám đốc. Link @handle / tên tuỳ chỉnh tra ra
 * mã kênh bằng kho khoá YouTube (1 đơn vị).
 */
export const downloadChannelVideosSchema = z.object({
    /** Link kênh (@handle, /channel/UC…, /user/…, /c/…) hoặc link một video của kênh; '' khi đã có `channelId`. */
    link: text(500).default(''),
    /** Mã kênh đã nối chọn thẳng — ưu tiên hơn `link`. */
    channelId: text(64).default(''),
    limit: z.union([z.literal(100), z.literal(200), z.literal(500)]).default(100),
});
/** THÔNG TIN VIDEO (29/10/2026, chủ dự án: «lấy thông tin video Tiêu đề, mô tả, thẻ tag / Thông tin tag kênh»). */
export const downloadInfoSchema = z.object({ videoId: z.string().regex(VIDEO_ID_RE) });
/**
 * THÔNG TIN KÊNH THEO LINK (30/10/2026, chủ dự án dán link kênh ngoài rồi «không đọc được thông tin kênh»): kênh BẤT KỲ
 * công khai — tên, @handle, mô tả, tag kênh, số liệu — hỏi bằng kho khoá công ty (1 đơn vị). Liệt kê video vẫn chỉ
 * kênh công ty đã nối (`connected`).
 */
export const downloadChannelInfoSchema = z.object({ link: text(500).min(1) });
export const downloadCreateSchema = z.object({
    agentId: z.number().int().positive(),
    links: z.array(text(500)).min(1).max(DOWNLOAD_MAX_LINKS),
    /** Tiêu đề · tên kênh đã biết (từ danh sách kênh / đọc link) — có thì máy chủ khỏi hỏi oEmbed từng video. */
    sources: z
        .array(z.object({
        videoId: z.string().regex(VIDEO_ID_RE),
        title: text(300).default(''),
        channel: text(200).default(''),
        /** Chất lượng riêng của video này (28/10/2026: chọn ngay trên dòng); thiếu thì theo `maxHeight` chung. */
        maxHeight: heightSchema.optional(),
    }))
        .max(DOWNLOAD_MAX_LINKS)
        .default([]),
    /** Thư mục trên máy công ty; '' = cầu nối tự chọn (`DOWNLOAD_DEFAULT_OUTPUT_HINT`). */
    outputDir: text(500).default(''),
    maxHeight: heightSchema.default(1080),
    /** Số thợ chạy cùng lúc trên máy (mỗi thợ một video). */
    parallel: z.number().int().min(1).max(3).default(1),
});
export const downloadJobParamsSchema = z.object({ jobId: z.coerce.number().int().positive() });
export const downloadProgressStages = [
    'downloading',
    'rendering',
    'ready',
    'uploading',
    'done',
    'failed',
];
export const downloadProgressSchema = z.object({
    jobId: z.number().int().positive(),
    stage: z.enum(downloadProgressStages),
    percent: z.number().min(0).max(100).default(0),
    note: text(300).default(''),
    error: text(1000).default(''),
    outputDir: text(500).default(''),
    outputName: text(300).default(''),
    resultVideoId: text(32).default(''),
    /** Gửi MỘT lần khi tải về xong: thông tin video gốc đọc từ yt-dlp — máy chủ trả tiêu đề · mô tả · tag + seo.txt. */
    source: z
        .object({
        title: text(500),
        description: z.string().max(20_000).default(''),
        tags: z.array(text(100)).max(300).default([]),
        durationSec: z.number().nonnegative().default(0),
        width: z.number().int().nonnegative().default(0),
        height: z.number().int().nonnegative().default(0),
        channel: text(200).default(''),
    })
        .optional(),
});
// ---------------------------------------------------------------- hàm thuần dùng ở cả web và máy chủ
/**
 * Tách danh sách link video dán vào: mỗi dòng một link (hoặc mã 11 ký tự), bỏ dòng trống, bỏ trùng trong cùng lượt.
 * Dòng không đọc ra mã video đưa vào `rejected` kèm lý do để người dùng sửa.
 */
export function parseDownloadLinks(links) {
    const seen = new Set();
    const items = [];
    const rejected = [];
    for (const raw of links) {
        const link = raw.trim();
        if (!link)
            continue;
        const videoId = extractYoutubeVideoId(link);
        if (!videoId) {
            rejected.push({ link, reason: 'Không đọc được mã video YouTube trong dòng này' });
            continue;
        }
        if (seen.has(videoId)) {
            rejected.push({ link, reason: 'Trùng với dòng trên' });
            continue;
        }
        seen.add(videoId);
        items.push({ link, videoId });
    }
    return { items, rejected };
}
/** Dòng dán vào là link KÊNH (không phải link video): youtube.com/@tên, /channel/UC…, /c/…, /user/…. */
export function isChannelLink(line) {
    const value = line.trim();
    if (!value || extractYoutubeVideoId(value))
        return false;
    return /youtube\.com\/(@[\w.-]+|channel\/UC[\w-]{22}|c\/[^/?#]+|user\/[^/?#]+)/i.test(value);
}
export const youtubeWatchUrl = (videoId) => `https://www.youtube.com/watch?v=${videoId}`;
export const downloadThumbnailUrl = (videoId) => `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
/**
 * Tên file / thư mục an toàn trên Windows từ một tiêu đề: bỏ ký tự cấm (`<>:"/\|?*`), ký tự điều khiển, dấu chấm
 * và khoảng trắng ở cuối (Windows không cho), gom khoảng trắng, cắt còn 80 ký tự. Trống thì dùng `fallback`.
 */
export function safeFileStem(title, fallback) {
    const cleaned = title
        .replace(/[<>:"/\\|?*]/g, ' ')
        .replace(/\p{Cc}/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 80)
        .replace(/[\s.]+$/g, '');
    return cleaned || fallback;
}
/**
 * Nội dung «<tên> - seo.txt» đúng dạng tool ToolNhacViet xuất ra (ba khối, mỗi khối mở đầu bằng dòng đếm) — Tải lên
 * của Đăng video (`parseSeoText`) và cầu nối (`Parse-Seo`) đều đọc lại được.
 */
export function buildSeoText(meta) {
    const tags = meta.tags.join(', ');
    return [
        '===== TIÊU ĐỀ =====',
        `${meta.title.length}/${YOUTUBE_TITLE_MAX} ký tự`,
        meta.title,
        '',
        '===== MÔ TẢ =====',
        `${meta.description.length}/${YOUTUBE_DESCRIPTION_MAX} ký tự`,
        meta.description,
        '',
        '===== TAG =====',
        `${meta.tags.length} tag · ${tagsLength(meta.tags)}/${YOUTUBE_TAGS_MAX} ký tự`,
        tags,
        '',
    ].join('\n');
}
/** «1:02:03» / «4:31». */
export function formatDownloadDuration(seconds) {
    const total = Math.max(0, Math.round(seconds));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const mm = h ? String(m).padStart(2, '0') : String(m);
    return `${h ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}
//# sourceMappingURL=video-download.js.map